import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/kit/Button';
import { Icon } from '@/kit/Icon';
import { InfoHint } from '@/kit/InfoHint';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { NotificationTextMessage } from '@/core/enums/notification';
import { notifyError } from '@/core/notify/notify';
import { selectedClient, type SelectedClient } from '@/core/selectedClient';

/** Where the X of several report settings pages goes (Angular `SETTINGS_ROUTE`). */
export const SETTINGS_ROUTE = '/home/taxreturns/tasks-accountingperiods';

/**
 * The client guard every page of this group runs in `ngOnInit`: no selected client -> error toast and back to Accounts.
 * The client is read once, like Angular did.
 */
export function useReportClient(onMissing?: () => void): SelectedClient | null {
  const navigate = useNavigate();
  const [client] = useState(() => selectedClient());
  useEffect(() => {
    if (client) return;
    notifyError('Select a client from Accounts first.');
    onMissing?.();
    navigate('/home/accountclient');
    // Runs once on mount, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return client;
}

/** Angular `FormValidationService.validateForm`: true when valid, otherwise the mandatory-fields toast. */
export function validateForm(isValid: boolean): boolean {
  if (isValid) return true;
  notifyError(NotificationTextMessage.validationMessage);
  return false;
}

interface FrameProps {
  title: string;
  /** Extra class on `.sidenav-details`: the root class a ported component `.scss` is wrapped in. */
  hostClassName?: string;
  /** The X button; omit for the pages whose X has no click handler in Angular. */
  onClose?: () => void;
  children: ReactNode;
}

/** The frame every Report Settings page shares: the settings menu, then `sidenav-details` with the title row. */
export function ReportSettingsFrame({ title, hostClassName = '', onClose, children }: FrameProps) {
  return (
    <SideMenuPage menu="settings">
      <div className={`sidenav-details ${hostClassName}`.trim()}>
        <div className="sidenav-header">
          <div className="d-flex align-items-center justify-space-between">
            {/* 2 Nov design review: the "Changes here apply..." banner is one small info icon beside the title. */}
            <div className="d-flex align-items-center gap-10">
              <h2>{title}</h2>
              <InfoHint text="Changes here apply to new reports and to any report you regenerate. Existing reports keep the settings they were made with." />
            </div>
            <Button className="close-btn" onClick={onClose}>
              <Icon name="close" />
            </Button>
          </div>
        </div>
        {children}
      </div>
    </SideMenuPage>
  );
}

/** Angular's `{{ number | number: '1.2-4' }}`; null/undefined show nothing. */
export function formatDecimal(value: number | null | undefined, minFraction: number, maxFraction: number): string {
  if (value === null || value === undefined) return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: minFraction,
    maximumFractionDigits: maxFraction,
  }).format(value);
}
